import type { ColumnDef } from '@tanstack/react-table';
import { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import DataGridTable from '@components/DataGridTable/DataGridTable';
import Icon from '@components/common/Icon';
import {
    TableRowActionsTooltipProvider,
    TableRowIconActions,
} from '@components/common/TableRowIconActions';
import Button from '@components/Button/Button';
import Input from '@/components/Form/Input/Input';
import PageHeader from '@components/common/PageHeader';
import ConfirmModal from '@components/common/ConfirmModal/ConfirmModal';
import { useAuth } from '@/context/AuthContext';
import { getApiErrorMessage } from '@/utils/apiValidationErrors';
import mentorsService, { type MentorDatum } from '@/services/mentors/mentorsService';
import CreateMentorModal, { type MentorForm } from './CreateMentorModal';
import EditMentorModal, { type EditMentorForm } from './EditMentorModal';
import AssignStudentsToMentorModal from '@/components/Mentors/AssignStudentsToMentorModal';
import MentorStudentsModal from '@/components/Mentors/MentorStudentsModal';

const MentorsPage = () => {
    const { t } = useTranslation('mentors');
    const queryClient = useQueryClient();
    const { isSuperAdmin } = useAuth();

    // Permissions — Admin can perform all actions
    const canCreate = isSuperAdmin;
    const canEdit = isSuperAdmin;
    const canDelete = isSuperAdmin;

    // Filters and pagination state
    const [search, setSearch] = useState('');
    const [pageIndex, setPageIndex] = useState(0);
    const [debouncedSearch, setDebouncedSearch] = useState(search);
    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [editingMentor, setEditingMentor] = useState<MentorDatum | null>(null);
    const [assignMentor, setAssignMentor] = useState<{ id: string; name: string } | null>(null);
    const [viewMentorStudents, setViewMentorStudents] = useState<{ id: string; name: string } | null>(null);
    const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
    const PAGE_SIZE = 20;

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const listQueryKey = ['mentors', pageIndex, PAGE_SIZE, debouncedSearch];

    const { data: mentorsResponse, isLoading, isError } = useQuery({
        queryKey: listQueryKey,
        queryFn: () =>
            mentorsService.getMentors(
                pageIndex + 1,
                PAGE_SIZE,
                debouncedSearch.trim() || undefined,
            ),
        placeholderData: previousData => previousData,
        staleTime: 5 * 60 * 1000,
    });

    const mentors = useMemo<MentorDatum[]>(
        () => mentorsResponse?.data?.data ?? [],
        [mentorsResponse],
    );

    const editMentorInitialData = useMemo<EditMentorForm | null>(
        () =>
            editingMentor
                ? {
                      name: editingMentor.name,
                      email: editingMentor.email,
                      speciality: editingMentor.speciality,
                  }
                : null,
        [editingMentor],
    );

    const createMentorMutation = useMutation({
        mutationFn: async (payload: MentorForm) =>
            await mentorsService.createMentor({
                name: payload.name,
                email: payload.email,
                speciality: payload.speciality,
            }),
        onSuccess: (response) => {
            toast.success(response.message ?? t('toast.create_success'));
            setShowCreate(false);
            queryClient.invalidateQueries({ queryKey: ['mentors'] });
        },
        onError: (error: unknown) => {
            toast.error(getApiErrorMessage(error, t('toast.create_error')));
        },
    });

    const updateMentorMutation = useMutation({
        mutationFn: ({ id, payload }: { id: string; payload: EditMentorForm }) =>
            mentorsService.updateMentor(id, {
                name: payload.name,
                email: payload.email,
                speciality: payload.speciality,
            }),
        onSuccess: (response) => {
            toast.success(response.message ?? t('toast.update_success'));
            setShowEdit(false);
            setEditingMentor(null);
            queryClient.invalidateQueries({ queryKey: ['mentors'] });
        },
        onError: (error: unknown) => {
            toast.error(getApiErrorMessage(error, t('toast.update_error')));
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => mentorsService.deleteMentor(id),
        onSuccess: () => {
            toast.success(t('toast.delete_success'));
            void queryClient.invalidateQueries({ queryKey: ['mentors'] });
        },
        onError: (error: { response?: { data?: { message?: string } } }) => {
            toast.error(error?.response?.data?.message ?? t('toast.delete_error'));
        },
    });

    const columns = useMemo<ColumnDef<MentorDatum, unknown>[]>(() => {
        const cols: ColumnDef<MentorDatum, unknown>[] = [
            {
                accessorKey: 'name',
                header: t('table.name'),
                size: 250,
                cell: ({ row }) => (
                    <span className="font-bold text-[13px] text-[var(--secondary-color)]">
                        {row.original.name}
                    </span>
                ),
            },
            {
                accessorKey: 'email',
                header: t('table.email'),
                size: 300,
                cell: ({ row }) => (
                    <span className="text-[13px] text-[var(--subtitle-color)]">
                        {row.original.email}
                    </span>
                ),
            },
            {
                accessorKey: 'speciality',
                header: t('table.speciality'),
                size: 250,
                cell: ({ row }) => (
                    <span className="text-[13px] text-[var(--secondary-color)] font-semibold px-2.5 py-1 rounded-full bg-blue-50 border border-blue-100">
                        {row.original.speciality}
                    </span>
                ),
            },
        ];

        if (canEdit || canDelete) {
            cols.push({
                id: 'actions',
                header: t('table.action'),
                size: 200,
                minSize: 200,
                enableSorting: false,
                cell: ({ row }) => {
                    const mentor = row.original;
                    const actions = [
                        {
                            icon: 'users' as const,
                            label: 'View Assigned Students',
                            onClick: () => {
                                setViewMentorStudents({ id: mentor.id, name: mentor.name });
                            },
                        },
                        {
                            icon: 'teamassignemeticon' as const,
                            label: 'Assign Students',
                            onClick: () => {
                                setAssignMentor({ id: mentor.id, name: mentor.name });
                            },
                        },
                        ...(canEdit
                            ? [
                                {
                                    icon: 'edit' as const,
                                    label: t('actions.edit'),
                                    onClick: () => {
                                        setEditingMentor(mentor);
                                        setShowEdit(true);
                                    },
                                },
                              ]
                            : []),
                        ...(canDelete
                            ? [
                                {
                                    icon: 'delete' as const,
                                    label: t('actions.delete'),
                                    onClick: () => setPendingDeleteId(mentor.id),
                                    variant: 'danger' as const,
                                },
                              ]
                            : []),
                    ];
                    return <TableRowIconActions actions={actions} />;
                },
            });
        }

        return cols;
    }, [canEdit, canDelete, t, setAssignMentor, setViewMentorStudents]);

    const searchIcon = (
        <button className="flex items-center cursor-pointer">
            <Icon name='search' className='w-3 2xl:w-[14px] h-auto' />
        </button>
    );

    return (
        <TableRowActionsTooltipProvider>
            <>
                <PageHeader
                    title={t('page_title')}
                    breadcrumbItems={[
                        { label: t('breadcrumb'), to: '/admin/dashboard' },
                        { label: t('page_title') },
                    ]}
                    actions={
                        <>
                            {canCreate && (
                                <Button onClick={() => setShowCreate(true)}>
                                    <Icon name="plus" className='mr-1 md:mr-2 mt-[-1px] w-3 2xl:w-4 h-auto' />
                                    {t('buttons.create')}
                                </Button>
                            )}
                        </>
                    }
                />
                <div className="flex flex-col gap-4 2xl:gap-5 !pt-0 p-3 md:p-4 2xl:p-5">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 lg:gap-4 2xl:gap-5 p-3 2xl:p-5 pb-[2px] 2xl:pb-[5px] bg-white rounded-2xl border border-[var(--neutral-color)]">
                        <div className='lg:col-start-4 lg:col-span-2'>
                            <Input
                                type="text"
                                placeholder={t('filters.search_placeholder')}
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                leftElement={searchIcon}
                            />
                        </div>
                    </div>
                    {isError ? (
                        <div className="bg-white rounded-2xl border border-[var(--neutral-color)] p-4 text-sm text-[var(--semantic-error-color)]">
                            {t('errors.load_failed')}
                        </div>
                    ) : (
                        <DataGridTable
                            data={mentors}
                            columns={columns}
                            totalCount={mentorsResponse?.data?.total ?? 0}
                            pageIndex={pageIndex}
                            pageSize={PAGE_SIZE}
                            onPageChange={p => setPageIndex(p)}
                            loading={isLoading}
                        />
                    )}
                </div>
                <CreateMentorModal
                    isOpen={showCreate}
                    onClose={() => setShowCreate(false)}
                    onSubmit={async (formData) => {
                        await createMentorMutation.mutateAsync(formData);
                    }}
                    isSubmitting={createMentorMutation.isPending}
                />
                <EditMentorModal
                    isOpen={showEdit}
                    onClose={() => {
                        setShowEdit(false);
                        setEditingMentor(null);
                    }}
                    initialData={editMentorInitialData}
                    onSubmit={async (formData) => {
                        if (!editingMentor) return;
                        await updateMentorMutation.mutateAsync({
                            id: editingMentor.id,
                            payload: formData,
                        });
                    }}
                    isSubmitting={updateMentorMutation.isPending}
                />
                <ConfirmModal
                    isOpen={pendingDeleteId !== null}
                    onClose={() => setPendingDeleteId(null)}
                    onConfirm={() => {
                        if (pendingDeleteId !== null) {
                            deleteMutation.mutate(pendingDeleteId);
                            setPendingDeleteId(null);
                        }
                    }}
                    loading={deleteMutation.isPending}
                    title={t('toast.delete_title')}
                    message={t('toast.delete_confirm')}
                    confirmLabel={t('actions.delete')}
                    variant="danger"
                />
                <AssignStudentsToMentorModal
                    isOpen={assignMentor !== null}
                    onClose={() => setAssignMentor(null)}
                    mentorId={assignMentor?.id ?? ''}
                    mentorName={assignMentor?.name ?? ''}
                />
                <MentorStudentsModal
                    isOpen={viewMentorStudents !== null}
                    onClose={() => setViewMentorStudents(null)}
                    mentorId={viewMentorStudents?.id ?? ''}
                    mentorName={viewMentorStudents?.name ?? ''}
                />
            </>
        </TableRowActionsTooltipProvider>
    );
};

export default MentorsPage;
